import { API_URLS, ARENA_GAME_TYPE, MAX_BET_REACHED_POPUP } from '@/utils/constant';
import {
  IArena,
  IFight,
  TaiXiuBetType,
  TaiXiuSelection,
  normalizeTaiXiuTicketBetType,
} from '@/utils/index.model';
import { assertBetAccepted, getData, keysToCamel, postData } from '@/utils/helpers';
import { getSubTickets, isTicketPending, normalizeTicket, subTicketsFromSummary } from '@/utils/ticket-selections';
import {
  ITaiXiuTable,
  TAI_XIU_BET_OPEN_STATUSES,
  TAI_XIU_CURRENT_ROUND_STATUSES,
  TAI_XIU_RESULT_STATUSES,
} from '@/utils/tai-xiu.model';

export type TaiXiuProduct = 'cockfight' | 'tai-xiu';

export type PaneId = 'a' | 'b';

export interface ISessionSlot {
  id: string;
  paneId: PaneId;
  product: TaiXiuProduct;
  tableId?: string;
}

// Phase 1 pins each product to a pane: A = Tai Xiu, B = cockfight. Slots are keyed by
// position rather than by product so a later phase can put two cockfights side by side
// without changing the pane contract -- which the old product-keyed `upsertSessionSlot`
// made structurally impossible.
const PANE_BY_PRODUCT: { [key in TaiXiuProduct]: PaneId } = {
  'tai-xiu': 'a',
  cockfight: 'b',
};

// A bet slip: the draft and placed bets for one table. The mobile board and
// the desktop quick-play card share the root slip (the fields on the state
// itself); each desktop pane showing a Tài Xỉu table owns one in `paneSlips`.
interface ITaiXiuSlip {
  draftBets: { [selection: string]: number };
  placedBets: { [selection: string]: number };
  betHistory: string[];
  feedback: string;
  isSubmitting: boolean;
}

export interface ITaiXiuPaneSlip extends ITaiXiuSlip {
  tableId: string;
  // The pane's own chip, so two Tài Xỉu panes (e.g. TXS and TON in the duo
  // view) do not share one. 0 = not picked yet: the shared `activeChip` stands in.
  activeChip: number;
}

export type TaiXiuPaneId = 'a' | 'b';

// The pill shown over a table's video (video-toast.vue): bet placed, payout
// (a win), or the unconfirmed bets dropped when betting closed.
export type TaiXiuVideoToastKind = 'placed' | 'payout' | 'cancelled';

export interface ITaiXiuVideoToast {
  key: number;
  kind: TaiXiuVideoToastKind;
  amount: number;
}

let videoToastKey = 0;

interface ITaiXiuState {
  tables: ITaiXiuTable[];
  paneSlips: { [paneId: string]: ITaiXiuPaneSlip };
  sessionSlots: ISessionSlot[];
  // Desktop split view only: how many panes are showing. Mobile ignores it.
  viewMode: 1 | 2;
  activeProduct: TaiXiuProduct;
  activeTableId: string;
  isTableBarOpen: boolean;
  quickPlayEnabled: boolean;
  quickBetTableId: string;
  activeChip: number;
  draftBets: { [selection: string]: number };
  placedBets: { [selection: string]: number };
  betHistory: string[];
  feedback: string;
  isSubmitting: boolean;
  isRolling: boolean;
  lastResult: number | null;
  roadChartFightIds: { [tableId: string]: string[] };
  videoToasts: { [tableId: string]: ITaiXiuVideoToast };
}

interface ITaiXiuBetPayload {
  amount: number;
  fight_id: string;
  selection: string;
  bet_type: TaiXiuBetType;
  odds_type: string;
}

const MAX_SESSION_SLOTS = 2;
const MAX_SELECTIONS_PER_BET = 10;
const ROAD_CHART_RESULT_LIMIT = 66;
// Betting window when an auto-closing arena sends no `auto_close_timer_sec`.
const DEFAULT_AUTO_CLOSE_SECONDS = 15;
let countdownTicker: number | null = null;

const secondsUntil = (closesAt?: number): number =>
  closesAt ? Math.max(0, Math.ceil((closesAt - Date.now()) / 1000)) : 0;

/*
 * When an open round's betting closes: its open time plus the arena's window.
 * The open time is the fight's `confirmedDateTime` (UTC, sent without a zone),
 * falling back to now -- a page loaded mid-round, or a time that does not
 * parse, starts the clock on first sight rather than showing nothing.
 */
const getBettingClosesAt = (table: ITaiXiuTable, fight: Partial<IFight>): number => {
  const raw = fight.confirmedDateTime ? String(fight.confirmedDateTime) : '';
  const openedAt = raw ? Date.parse(/(Z|[+-]\d{2}:?\d{2})$/.test(raw) ? raw : `${raw}Z`) : NaN;
  const windowMs = table.autoCloseSeconds * 1000;
  const now = Date.now();
  const isSane = Number.isFinite(openedAt) && openedAt <= now && now - openedAt <= windowMs;
  return (isSane ? openedAt : now) + windowMs;
};
// Mutually exclusive markets: betting one side rules out the other for the
// round. Exported so the board can block the opposite option too.
export const EXCLUSIVE_SELECTIONS: { [selection: string]: string } = {
  tai: 'xiu',
  xiu: 'tai',
  odd: 'even',
  even: 'odd',
};

const getTaiXiuTotal = (result: unknown): number | undefined => {
  const values = Array.isArray(result)
    ? result.map(Number)
    : String(result || '')
      .split(/[\s,|/-]+/)
      .filter(Boolean)
      .map(Number);
  if (!values.length || values.some((value) => !Number.isFinite(value))) return;

  // The socket may send either three dice, or [total, die 1, die 2].
  const total = values.length === 3 && values.every((value) => value >= 1 && value <= 6)
    ? values.reduce((sum, value) => sum + value, 0)
    : values[0];
  return total >= 4 && total <= 17 ? total : undefined;
};

export const getTaiXiuDice = (result: unknown): number[] | undefined => {
  const values = Array.isArray(result)
    ? result.map(Number)
    : String(result || '')
      .split(/[\s,|/-]+/)
      .filter(Boolean)
      .map(Number);
  if (values.length !== 3 || values.some((value) => !Number.isInteger(value))) return undefined;
  if (values.every((value) => value >= 1 && value <= 6)) return values;

  // Socket format: [total, die 1, die 2]. Infer the third die.
  const [total, first, second] = values;
  const third = total - first - second;
  return total >= 4 && total <= 17 && [first, second, third].every((face) => face >= 1 && face <= 6)
    ? [first, second, third]
    : undefined;
};

/*
 * The newest result known for a table, as three dice.
 *
 * Reads the road chart rather than `table.result`, because the chart is written
 * by both paths -- the socket update and the road-chart fetch -- while
 * `table.result` is only written by the socket and is cleared when a new round
 * starts. Falls back to `table.result` for a table with no chart yet.
 */
export const getLatestTableDice = (table: any): number[] | undefined => {
  const recentDice = Array.isArray(table && table.recentDice) ? table.recentDice : [];
  for (let index = recentDice.length - 1; index >= 0; index -= 1) {
    const dice = getTaiXiuDice(recentDice[index]);
    if (dice) return dice;
  }
  return getTaiXiuDice(table && table.result);
};

/*
 * A table chip's status colour, on the cockfight arena rail's palette
 * (`bet-panel/arena-status-indicator.vue`): the result's colour once there is
 * one -- BIG red and SMALL blue, as on the road -- orange while betting is open,
 * otherwise none. As on cockfight the result holds until the next round opens.
 */
export const getTableStatusClass = (table: any): string => {
  if (!table) return '';
  let total = getTaiXiuTotal(table.result);
  // A table loaded mid-result has no socket result yet; the road has it.
  if (total === undefined && TAI_XIU_RESULT_STATUSES.includes(String(table.fightStatus))) {
    const dice = getLatestTableDice(table);
    if (dice) total = dice[0] + dice[1] + dice[2];
  }
  if (total !== undefined) return total > 10 ? 'red' : 'blue';
  return TAI_XIU_BET_OPEN_STATUSES.includes(String(table.fightStatus)) ? 'orange' : '';
};

/*
 * The desktop view (single / double) is the member's preference, so it outlives
 * a refresh and a fresh game launch -- localStorage, not the session-scoped
 * vuex persistence. Double view until the member picks one.
 */
const VIEW_MODE_KEY = 'taiXiu.viewMode';

const readViewMode = (): 1 | 2 => {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === '1' ? 1 : 2;
  } catch (error) {
    return 2;
  }
};

const writeViewMode = (mode: 1 | 2): void => {
  try {
    window.localStorage.setItem(VIEW_MODE_KEY, String(mode));
  } catch (error) {
    // Private mode, blocked storage: the choice still holds for this page view.
  }
};

const emptyPaneSlip = (tableId = '', activeChip = 0): ITaiXiuPaneSlip => ({
  tableId,
  activeChip,
  draftBets: {},
  placedBets: {},
  betHistory: [],
  feedback: '',
  isSubmitting: false,
});

const initialState = (): ITaiXiuState => ({
  tables: [],
  paneSlips: { a: emptyPaneSlip(), b: emptyPaneSlip() },
  sessionSlots: [{ id: 'cockfight', paneId: PANE_BY_PRODUCT.cockfight, product: 'cockfight' }],
  viewMode: readViewMode(),
  activeProduct: 'cockfight',
  activeTableId: '',
  isTableBarOpen: false,
  quickPlayEnabled: false,
  quickBetTableId: '',
  activeChip: 5,
  draftBets: {},
  placedBets: {},
  betHistory: [],
  feedback: '',
  isSubmitting: false,
  isRolling: false,
  lastResult: null,
  roadChartFightIds: {},
  videoToasts: {},
});

const getBetPayload = (
  draftSelection: string,
  amount: number,
  fightId: string
): Omit<ITaiXiuBetPayload, 'odds_type'> | undefined => {
  const fixedSelections: { [key: string]: { selection: TaiXiuSelection; betType: TaiXiuBetType } } = {
    tai: { selection: TaiXiuSelection.big, betType: TaiXiuBetType.bigSmall },
    xiu: { selection: TaiXiuSelection.small, betType: TaiXiuBetType.bigSmall },
    odd: { selection: TaiXiuSelection.odd, betType: TaiXiuBetType.oddEven },
    even: { selection: TaiXiuSelection.even, betType: TaiXiuBetType.oddEven },
  };
  const fixedSelection = fixedSelections[draftSelection];
  if (fixedSelection) {
    return {
      amount,
      fight_id: fightId,
      selection: fixedSelection.selection,
      bet_type: fixedSelection.betType,
    };
  }

  const [market, rawValue] = draftSelection.split('-');
  const selection = Number(rawValue);
  if (!Number.isInteger(selection)) return;

  if (market === 'sum' && selection >= 4 && selection <= 17) {
    return {
      amount,
      fight_id: fightId,
      selection: rawValue,
      bet_type: (TaiXiuBetType.total4 + selection - 4) as TaiXiuBetType,
    };
  }
  if (market === 'single' && selection >= 1 && selection <= 6) {
    return { amount, fight_id: fightId, selection: rawValue, bet_type: TaiXiuBetType.single };
  }
  if (market === 'triple' && selection >= 1 && selection <= 6) {
    return { amount, fight_id: fightId, selection: rawValue, bet_type: TaiXiuBetType.triple };
  }
  if (market === 'domino') {
    return { amount, fight_id: fightId, selection: rawValue, bet_type: TaiXiuBetType.domino };
  }
  if (market === 'double') {
    return { amount, fight_id: fightId, selection: rawValue, bet_type: TaiXiuBetType.double };
  }
};

const upsertSessionSlot = (slots: ISessionSlot[], slot: ISessionSlot): ISessionSlot[] => {
  const withoutSamePane = slots.filter((item) => item.paneId !== slot.paneId);
  const nextSlots = [...withoutSamePane, slot];
  return nextSlots.slice(Math.max(0, nextSlots.length - MAX_SESSION_SLOTS));
};

const closeBetBoard = (stateData: ITaiXiuState): void => {
  // Dismisses the bet board (quick play sheet or full board) and nothing else:
  // the Tài Xỉu table bar stays up so the member can pick another table or
  // leave through the dice. Quick play stays on if it was on.
  stateData.quickBetTableId = '';
  stateData.activeProduct = 'cockfight';
  stateData.isTableBarOpen = true;
  stateData.feedback = '';
  stateData.sessionSlots = upsertSessionSlot(stateData.sessionSlots, {
    id: 'cockfight',
    paneId: PANE_BY_PRODUCT.cockfight,
    product: 'cockfight',
  });
};

const addBetToSlip = (slip: ITaiXiuSlip, selection: string, chip: number): void => {
  const oppositeSelection = EXCLUSIVE_SELECTIONS[selection];

  // A placed bet on the opposite side is final for the round, so this side
  // is out -- the board disables it, and this guards the action itself.
  if (oppositeSelection && slip.placedBets[oppositeSelection]) {
    slip.feedback = 'You already have a bet on the opposite option';
    return;
  }

  const nextBets = { ...slip.draftBets };

  if (oppositeSelection && nextBets[oppositeSelection]) {
    delete nextBets[oppositeSelection];
    slip.betHistory = slip.betHistory.filter((item) => item !== oppositeSelection);
  }

  if (!nextBets[selection] && Object.keys(nextBets).length >= MAX_SELECTIONS_PER_BET) {
    slip.feedback = `You can select up to ${MAX_SELECTIONS_PER_BET} bets at a time`;
    return;
  }

  slip.draftBets = {
    ...nextBets,
    [selection]: (nextBets[selection] || 0) + chip,
  };
  slip.betHistory.push(selection);
  slip.feedback = '';
};

/*
 * A selection's per-round limit: its bet type's limit from bet-type-odds
 * (`table.betLimits`), which is what the backend enforces (E19) -- Tài took
 * 1,000 while the header's cockfight MAX said 30, so that MAX is not used. The
 * arena's own limit is only a fallback for a bet type that has none.
 * 0 = no limit known.
 */
const selectionLimit = (table: ITaiXiuTable | undefined, selection: string): number => {
  if (!table) return 0;
  const payload = getBetPayload(selection, 0, '');
  const betTypeLimit = payload && table.betLimits ? Number(table.betLimits[payload.bet_type]) || 0 : 0;
  return betTypeLimit || Number(table.maxBet) || 0;
};

// The chip a selection can still take: its limit less what is already drafted
// and placed on it this round, so a 15,000 limit with 9,000 placed takes at most
// 6,000 more. Zero means the selection is full.
const allowedChip = (slip: ITaiXiuSlip, max: number, selection: string, chip: number): number => {
  if (!max) return chip;
  const used = (slip.draftBets[selection] || 0) + (slip.placedBets[selection] || 0);
  return Math.max(0, Math.min(chip, max - used));
};


// A desktop pane's chip, or the shared one until the pane has picked its own.
const paneChip = (stateData: ITaiXiuState, slip: ITaiXiuPaneSlip): number =>
  slip.activeChip || stateData.activeChip;

const undoSlipBet = (slip: ITaiXiuSlip, chip: number): void => {
  const selection = slip.betHistory.pop();
  if (!selection) return;
  const nextAmount = (slip.draftBets[selection] || 0) - chip;
  const nextBets = { ...slip.draftBets };
  if (nextAmount > 0) {
    nextBets[selection] = nextAmount;
  } else {
    delete nextBets[selection];
  }
  slip.draftBets = nextBets;
  slip.feedback = '';
};

const clearSlip = (slip: ITaiXiuSlip): void => {
  slip.draftBets = {};
  slip.betHistory = [];
  slip.feedback = '';
};

const addPlacedBetsToSlip = (slip: ITaiXiuSlip, bets: { [selection: string]: number }): void => {
  const placedBets = { ...slip.placedBets };
  Object.keys(bets).forEach((selection) => {
    placedBets[selection] = (placedBets[selection] || 0) + bets[selection];
  });
  slip.placedBets = placedBets;
};

/*
 * Betting closed: chips the member picked but never submitted can no longer be
 * placed, so they go -- in every slip showing the table: the root one (quick
 * play, or the mobile board) and any desktop pane. Placed bets stay; they are
 * confirmed for the round. Only touched when there is something to drop, since
 * this runs on every socket tick of a closed round.
 */
const clearDraftsForTable = (stateData: ITaiXiuState, tableId: string): void => {
  const slips: ITaiXiuSlip[] = [
    ...((stateData.quickBetTableId || stateData.activeTableId) === tableId ? [stateData] : []),
    ...Object.keys(stateData.paneSlips)
      .map((paneId) => stateData.paneSlips[paneId])
      .filter((slip) => slip.tableId === tableId),
  ];
  let droppedDraft = false;
  slips.forEach((slip) => {
    if (!Object.keys(slip.draftBets).length && !slip.betHistory.length) return;
    if (Object.keys(slip.draftBets).length) droppedDraft = true;
    slip.draftBets = {};
    slip.betHistory = [];
  });
  // Betting closed with bets selected but never confirmed: say so on the video.
  if (droppedDraft) {
    videoToastKey += 1;
    stateData.videoToasts = {
      ...stateData.videoToasts,
      [tableId]: { key: videoToastKey, kind: 'cancelled', amount: 0 },
    };
  }
};

// A new round (or a cleared one) voids the table's draft and placed bets in
// every slip on that table, not just the root one.
const resetPaneSlipsForTable = (stateData: ITaiXiuState, tableId: string, keepFeedback = false): void => {
  Object.keys(stateData.paneSlips).forEach((paneId) => {
    const slip = stateData.paneSlips[paneId];
    if (slip.tableId !== tableId) return;
    slip.draftBets = {};
    slip.placedBets = {};
    slip.betHistory = [];
    if (!keepFeedback) slip.feedback = '';
  });
};

/*
 * Placed bets after a reload.
 *
 * `placedBets` lives only in memory, so a refresh lost the chips of bets the
 * backend had already confirmed for the running round. They are rebuilt from
 * the member's tickets: each ticket's `bet_type` + `selection` maps back to the
 * board key it was placed from (the inverse of `getBetPayload`).
 */
// Far enough back to cover any round still running.
const RESTORE_LOOKBACK_MS = 30 * 60 * 1000;
const RESTORE_PAGE_SIZE = 100;
// Tables report their round one by one on load; one fetch covers the burst.
const RESTORE_DEBOUNCE_MS = 800;
let restoreTimer: ReturnType<typeof setTimeout> | null = null;
// A failed fetch is retried a few times, so one dropped request does not leave
// a confirmed bet off the board until the next reload.
const RESTORE_RETRY_DELAY_MS = 3000;
const RESTORE_MAX_RETRIES = 3;
let restoreRetries = 0;
// `${slip}:${fightId}` already restored, so each round is fetched once per slip.
const restoredRounds = new Set<string>();

/*
 * Pointing a slip at a table empties its placed bets (SELECT_TABLE,
 * SET_QUICK_PLAY_TABLE, SET_PANE_TABLE) -- on mobile, every tap that opens the
 * board. The slip has to be restorable again, or a round already restored once
 * stays empty: reopening the board after a reload lost the confirmed chips.
 */
const forgetRestoredSlip = (slipKey: string): void => {
  Array.from(restoredRounds)
    .filter((roundKey) => roundKey.startsWith(`${slipKey}:`))
    .forEach((roundKey) => restoredRounds.delete(roundKey));
};

/*
 * A ticket's bet type comes in either numbering (normalizeTaiXiuTicketBetType).
 * The selection comes back in either form -- the placed one (`big`, `odd`) or
 * the board's (`tai`, `lẻ`) -- so both are read, as the rail's labels do.
 */
const TICKET_SELECTION_KEYS: { [selection: string]: string } = {
  [TaiXiuSelection.big]: 'tai',
  tai: 'tai',
  tài: 'tai',
  [TaiXiuSelection.small]: 'xiu',
  xiu: 'xiu',
  xỉu: 'xiu',
  [TaiXiuSelection.odd]: 'odd',
  le: 'odd',
  lẻ: 'odd',
  [TaiXiuSelection.even]: 'even',
  chan: 'even',
  chẵn: 'even',
};

const ticketBetTypeToSelection = (ticketBetType: number, selection: string): string | undefined => {
  const value = String(selection || '').toLowerCase();
  const betType = normalizeTaiXiuTicketBetType(ticketBetType, selection);
  if (betType === undefined) return;
  if (betType === TaiXiuBetType.bigSmall) {
    const key = TICKET_SELECTION_KEYS[value];
    return key === 'tai' || key === 'xiu' ? key : undefined;
  }
  if (betType === TaiXiuBetType.oddEven) {
    const key = TICKET_SELECTION_KEYS[value];
    return key === 'odd' || key === 'even' ? key : undefined;
  }
  if (betType >= TaiXiuBetType.total4 && betType <= TaiXiuBetType.total17) {
    return `sum-${betType - TaiXiuBetType.total4 + 4}`;
  }
  if (betType === TaiXiuBetType.domino) return `domino-${value}`;
  // A face market takes a die face; anything else is not its selection.
  const face = Number(value);
  if (!Number.isInteger(face) || face < 1 || face > 6) return;
  if (betType === TaiXiuBetType.single) return `single-${value}`;
  if (betType === TaiXiuBetType.triple) return `triple-${value}`;
  if (betType === TaiXiuBetType.double) return `double-${value}`;
};

/*
 * As the rail reads it (taiXiuSelectionMarket): a 4..17 selection whose bet
 * type does not fit it is a total. A multi-selection ticket's sub-ticket can
 * carry its parent's bet type (Small's, say, on a "14"), which left the total's
 * chip off the board after a reload.
 */
const ticketToSelection = (ticketBetType: number, selection: string): string | undefined => {
  const key = ticketBetTypeToSelection(ticketBetType, selection);
  if (key) return key;
  const total = Number(selection);
  return Number.isInteger(total) && total >= 4 && total <= 17 ? `sum-${total}` : undefined;
};

/*
 * A ticket's board selections and stakes. A multi-selection ticket carries
 * them in `sub_tickets` (the parent has bet type 0 and a "big:1.97|..." summary),
 * so reading only the parent restored nothing after a reload. A summary with no
 * sub-tickets has no per-selection stake; its total only counts when it holds
 * one selection.
 */
const ticketPlacedBets = (ticket: any): Array<{ selection: string; amount: number }> => {
  let parts: any[] = getSubTickets(ticket);
  if (!parts.length) {
    const derived = subTicketsFromSummary(ticket);
    parts = derived.length > 1 ? derived : [ticket];
  }
  return parts
    .filter((part) => part.status === undefined || isTicketPending(part))
    .map((part) => {
      // Splits a "big:1.97" selection and fills in the bet type from it.
      const sub = normalizeTicket({ ...part, subTickets: undefined }, false);
      return {
        selection: ticketToSelection(Number(sub.betType), String(sub.selection)) || '',
        amount: Number(part.amount) || 0,
      };
    })
    .filter((bet) => bet.selection && bet.amount > 0);
};

// Server totals win, but never below what this page has placed since -- a bet
// accepted while the fetch was in flight is not in its reply.
const mergePlacedBets = (slip: ITaiXiuSlip, bets: { [selection: string]: number }): void => {
  const placedBets = { ...slip.placedBets };
  Object.keys(bets).forEach((selection) => {
    placedBets[selection] = Math.max(placedBets[selection] || 0, bets[selection]);
  });
  slip.placedBets = placedBets;
};

/*
 * Whether a fight belongs to the table's current match day. A fight that does
 * not say, or a table whose match day is not known yet, passes: only a known
 * mismatch is rejected.
 */
const isFightOfMatchDay = (fight: Partial<IFight>, matchDayId?: string): boolean => {
  const fightMatchDayId = fight.matchDay && fight.matchDay.id;
  return !fightMatchDayId || !matchDayId || fightMatchDayId === matchDayId;
};

// A round's result into the table's road chart, updated in place if it is there.
const recordTableResult = (stateData: ITaiXiuState, table: ITaiXiuTable, fight: Partial<IFight>): void => {
  if (fight.result === undefined || !fight.id) return;
  const total = getTaiXiuTotal(fight.result);
  const dice = getTaiXiuDice(fight.result);
  if (total === undefined) return;
  const fightIds = stateData.roadChartFightIds[table.id] || [];
  const resultIndex = fightIds.indexOf(fight.id);
  if (resultIndex >= 0) {
    table.recentResults.splice(resultIndex, 1, total);
    table.recentDice.splice(resultIndex, 1, dice);
    if (table.recentRounds && fight.fightNumber !== undefined) {
      table.recentRounds.splice(resultIndex, 1, Number(fight.fightNumber));
    }
  } else {
    table.recentResults = [...table.recentResults, total].slice(-ROAD_CHART_RESULT_LIMIT);
    table.recentDice = [...table.recentDice, dice].slice(-ROAD_CHART_RESULT_LIMIT);
    table.recentRounds = [
      // Pad to the results already there, so the rounds stay in step with them.
      ...Array.from<number | undefined>({
        length: Math.max(0, table.recentResults.length - 1 - (table.recentRounds || []).length),
      }),
      ...(table.recentRounds || []),
      fight.fightNumber !== undefined ? Number(fight.fightNumber) : undefined,
    ].slice(-ROAD_CHART_RESULT_LIMIT);
    stateData.roadChartFightIds[table.id] = [...fightIds, fight.id].slice(-ROAD_CHART_RESULT_LIMIT);
  }
};

export const state = initialState;

export const mutations = {
  /*
   * Restored bets for one table's round, into every slip showing that table:
   * the root slip (quick play or the mobile board) and any desktop pane.
   */
  RESTORE_PLACED_BETS(
    stateData: ITaiXiuState,
    payload: { tableId: string; fightId: string; bets: { [selection: string]: number } }
  ): void {
    const table = stateData.tables.find((item) => item.id === payload.tableId);
    // The round moved on while the request was out: these bets are void.
    if (!table || table.currentFightId !== payload.fightId) return;
    if ((stateData.quickBetTableId || stateData.activeTableId) === payload.tableId) {
      mergePlacedBets(stateData, payload.bets);
    }
    Object.keys(stateData.paneSlips).forEach((paneId) => {
      const slip = stateData.paneSlips[paneId];
      if (slip.tableId === payload.tableId) mergePlacedBets(slip, payload.bets);
    });
  },
  SET_TABLE_BAR(stateData: ITaiXiuState, isOpen: boolean): void {
    stateData.isTableBarOpen = isOpen;
    if (!isOpen) {
      stateData.feedback = '';
    }
  },
  SET_QUICK_PLAY(stateData: ITaiXiuState, enabled: boolean): void {
    if (stateData.quickPlayEnabled === enabled) return;

    stateData.quickPlayEnabled = enabled;

    if (enabled) {
      if (stateData.activeProduct === 'tai-xiu' && stateData.activeTableId) {
        stateData.quickBetTableId = stateData.activeTableId;
        stateData.activeProduct = 'cockfight';
      }
      return;
    }

    if (stateData.quickBetTableId) {
      stateData.activeTableId = stateData.quickBetTableId;
      stateData.activeProduct = 'tai-xiu';
      stateData.quickBetTableId = '';
      stateData.isTableBarOpen = false;
    }
  },
  SELECT_TABLE(stateData: ITaiXiuState, tableId: string): void {
    if (!stateData.tables.some((table) => table.id === tableId)) { return; }

    const isQuickPlayTableOpen =
      stateData.quickPlayEnabled && stateData.quickBetTableId === tableId;
    const isFullModeTableActive =
      !stateData.quickPlayEnabled &&
      stateData.activeProduct === 'tai-xiu' &&
      stateData.activeTableId === tableId;

    if (isQuickPlayTableOpen || isFullModeTableActive) {
      closeBetBoard(stateData);
      return;
    }

    stateData.activeTableId = tableId;
    stateData.sessionSlots = upsertSessionSlot(stateData.sessionSlots, {
      id: `tai-xiu:${tableId}`,
      paneId: PANE_BY_PRODUCT['tai-xiu'],
      product: 'tai-xiu',
      tableId,
    });
    stateData.isTableBarOpen = false;
    if (stateData.quickPlayEnabled) {
      stateData.activeProduct = 'cockfight';
      stateData.quickBetTableId = tableId;
    } else {
      stateData.activeProduct = 'tai-xiu';
      stateData.quickBetTableId = '';
    }
    stateData.draftBets = {};
    stateData.placedBets = {};
    stateData.betHistory = [];
    stateData.feedback = '';
  },
  SET_VIEW_MODE(stateData: ITaiXiuState, mode: 1 | 2): void {
    stateData.viewMode = mode === 1 ? 1 : 2;
    writeViewMode(stateData.viewMode);
  },
  /*
   * Binds a desktop pane to a table (or unbinds it with an empty id).
   * Deliberately not SELECT_TABLE: that one also flips `activeProduct`, closes
   * the table bar and toggles quick play, which are mobile navigation side
   * effects. Each pane carries its own slip, so two panes can sit on two
   * tables and bet on each independently.
   */
  SET_PANE_TABLE(stateData: ITaiXiuState, payload: { paneId: TaiXiuPaneId; tableId: string }): void {
    const { paneId, tableId } = payload;
    if (tableId && !stateData.tables.some((table) => table.id === tableId)) return;
    const slip = stateData.paneSlips[paneId];
    if (slip && slip.tableId === tableId) return;
    // The pane keeps its chip across tables; the board re-picks one if the new
    // table's chip set does not have it.
    stateData.paneSlips = {
      ...stateData.paneSlips,
      [paneId]: emptyPaneSlip(tableId, slip ? slip.activeChip : 0),
    };
  },
  PANE_SET_ACTIVE_CHIP(stateData: ITaiXiuState, payload: { paneId: TaiXiuPaneId; amount: number }): void {
    const slip = stateData.paneSlips[payload.paneId];
    if (slip) slip.activeChip = payload.amount;
  },
  PANE_ADD_BET(stateData: ITaiXiuState, payload: { paneId: TaiXiuPaneId; selection: string; chip: number }): void {
    const slip = stateData.paneSlips[payload.paneId];
    if (slip) addBetToSlip(slip, payload.selection, payload.chip);
  },
  PANE_UNDO_BET(stateData: ITaiXiuState, paneId: TaiXiuPaneId): void {
    const slip = stateData.paneSlips[paneId];
    if (slip) undoSlipBet(slip, paneChip(stateData, slip));
  },
  PANE_CLEAR_BETS(stateData: ITaiXiuState, paneId: TaiXiuPaneId): void {
    const slip = stateData.paneSlips[paneId];
    if (slip) clearSlip(slip);
  },
  /*
   * A confirmed bet belongs to the table's round, not to the board it was sent
   * from: every slip showing that table gets it -- the root one (quick play or
   * the mobile board) and each desktop pane. Kept per slip, a bet placed from
   * the quick-play card left the pane on the same table without its chip (and
   * its Tài/Xỉu block).
   */
  SHOW_VIDEO_TOAST(
    stateData: ITaiXiuState,
    payload: { tableId: string; kind: TaiXiuVideoToastKind; amount?: number }
  ): void {
    if (!payload.tableId) return;
    videoToastKey += 1;
    stateData.videoToasts = {
      ...stateData.videoToasts,
      [payload.tableId]: { key: videoToastKey, kind: payload.kind, amount: payload.amount || 0 },
    };
  },
  ADD_TABLE_PLACED_BETS(
    stateData: ITaiXiuState,
    payload: { tableId: string; bets: { [selection: string]: number } }
  ): void {
    if ((stateData.quickBetTableId || stateData.activeTableId) === payload.tableId) {
      addPlacedBetsToSlip(stateData, payload.bets);
    }
    Object.keys(stateData.paneSlips).forEach((paneId) => {
      const slip = stateData.paneSlips[paneId];
      if (slip.tableId === payload.tableId) addPlacedBetsToSlip(slip, payload.bets);
    });
  },
  PANE_SET_FEEDBACK(stateData: ITaiXiuState, payload: { paneId: TaiXiuPaneId; feedback: string }): void {
    const slip = stateData.paneSlips[payload.paneId];
    if (slip) slip.feedback = payload.feedback;
  },
  PANE_SET_SUBMITTING(stateData: ITaiXiuState, payload: { paneId: TaiXiuPaneId; isSubmitting: boolean }): void {
    const slip = stateData.paneSlips[payload.paneId];
    if (slip) slip.isSubmitting = payload.isSubmitting;
  },
  /*
   * Points the desktop quick-play card at a table.
   *
   * Like SET_PANE_TABLE this avoids SELECT_TABLE's mobile navigation side
   * effects (flipping activeProduct, closing the table bar). It also does not
   * require quickPlayEnabled: on desktop the card is always on screen, so it
   * always needs a table, independent of the mobile quick-play toggle.
   */
  SET_QUICK_PLAY_TABLE(stateData: ITaiXiuState, tableId: string): void {
    if (!stateData.tables.some((table) => table.id === tableId)) return;
    if (stateData.quickBetTableId === tableId) return;

    stateData.quickBetTableId = tableId;
    stateData.draftBets = {};
    stateData.betHistory = [];
    stateData.feedback = '';
  },
  SELECT_COCKFIGHT(stateData: ITaiXiuState): void {
    // Switching back to cockfight only dismisses the bet board. The Tài Xỉu
    // table bar stays up -- toggling between the two products is not a request
    // to leave Tài Xỉu, so the member keeps the table list one tap away. Only
    // the dice launcher itself (toggleTableBar / closeTableBar) hides the bar.
    stateData.activeProduct = 'cockfight';
    stateData.quickBetTableId = '';
    stateData.isTableBarOpen = true;
    stateData.sessionSlots = upsertSessionSlot(stateData.sessionSlots, {
      id: 'cockfight',
      paneId: PANE_BY_PRODUCT.cockfight,
      product: 'cockfight',
    });
  },
  CLOSE_QUICK_BET(stateData: ITaiXiuState): void {
    closeBetBoard(stateData);
  },
  SET_ACTIVE_CHIP(stateData: ITaiXiuState, amount: number): void {
    stateData.activeChip = amount;
  },
  ADD_BET(stateData: ITaiXiuState, payload: { selection: string; chip: number }): void {
    addBetToSlip(stateData, payload.selection, payload.chip);
  },
  UNDO_BET(stateData: ITaiXiuState): void {
    undoSlipBet(stateData, stateData.activeChip);
  },
  CLEAR_BETS(stateData: ITaiXiuState): void {
    clearSlip(stateData);
  },
  REMOVE_BET_SELECTION(stateData: ITaiXiuState, selection: string): void {
    const nextBets = { ...stateData.draftBets };
    delete nextBets[selection];
    stateData.draftBets = nextBets;
    stateData.betHistory = stateData.betHistory.filter((item) => item !== selection);
  },
  SET_FEEDBACK(stateData: ITaiXiuState, feedback: string): void {
    stateData.feedback = feedback;
  },
  SET_SUBMITTING(stateData: ITaiXiuState, isSubmitting: boolean): void {
    stateData.isSubmitting = isSubmitting;
  },
  SET_ROLLING(stateData: ITaiXiuState, isRolling: boolean): void {
    stateData.isRolling = isRolling;
  },
  SET_RESULT(stateData: ITaiXiuState, result: number): void {
    stateData.lastResult = result;
    stateData.isRolling = false;
    stateData.feedback = '';
    const table = stateData.tables.find((item) => item.id === stateData.activeTableId);
    if (table) {
      table.recentResults = [...table.recentResults, result].slice(-ROAD_CHART_RESULT_LIMIT);
      stateData.roadChartFightIds[table.id] = [
        ...(stateData.roadChartFightIds[table.id] || []),
        '',
      ].slice(-ROAD_CHART_RESULT_LIMIT);
    }
    stateData.draftBets = {};
    stateData.betHistory = [];
  },
  SYNC_TABLE_ARENAS(stateData: ITaiXiuState, arenas: any[]): void {
    const previousTables = stateData.tables.reduce(
      (result: { [arenaId: string]: ITaiXiuTable }, table: ITaiXiuTable) => {
        result[table.arenaId] = table;
        return result;
      },
      {}
    );
    stateData.tables = arenas.map((arena: any, index: number) => {
      const previousTable = previousTables[arena.id];
      const rawName = typeof arena.name === 'string' ? arena.name : (arena.name || {}).default;
      const tableNumber = String(rawName || '').match(/\d+/);
      const matchDayChanged =
        !!previousTable &&
        !!previousTable.currentMatchDayId &&
        previousTable.currentMatchDayId !== arena.currentMatchDayId;
      if (matchDayChanged) stateData.roadChartFightIds[arena.id] = [];
      // The backend's own arena code ("txs") is the label; one is only derived
      // from the name ("Tai Xiu 1" -> "TX1") when the arena has none.
      const code = String(
        arena.code || (tableNumber ? `TX${tableNumber[0]}` : `TX${index + 1}`)
      ).toUpperCase();
      return {
        ...(previousTable || {}),
        id: arena.id,
        arenaId: arena.id,
        code,
        name: rawName || `Tài Xỉu ${index + 1}`,
        currentMatchDayId: arena.currentMatchDayId,
        currentFightId: matchDayChanged ? undefined : previousTable && previousTable.currentFightId,
        // The arenas endpoint carries no fight status: the round's comes from
        // the socket / current fight. Taken from the list, every arena turned on
        // or off (arena_visibility_updated reloads it) blanked it, and the board
        // dimmed as closed mid-round. Kept as cockfight's getArenas keeps it.
        fightStatus: matchDayChanged || !previousTable || previousTable.fightStatus === undefined
          ? arena.fightStatus
          : previousTable.fightStatus,
        result: matchDayChanged ? undefined : previousTable && previousTable.result,
        isAvailable: arena.status === undefined || Number(arena.status) === 1,
        round: matchDayChanged ? 0 : (previousTable && previousTable.round) || 0,
        secondsRemaining: matchDayChanged ? 0 : (previousTable && previousTable.secondsRemaining) || 0,
        autoClose: arena.automationAutoClose === true,
        autoCloseSeconds: Number(arena.autoCloseTimerSec) > 0
          ? Number(arena.autoCloseTimerSec)
          : DEFAULT_AUTO_CLOSE_SECONDS,
        bettingClosesAt: matchDayChanged ? undefined : previousTable && previousTable.bettingClosesAt,
        minBet: Number(arena.minLimitAmountPerFight || arena.minAmountPerFight || arena.minBet || 0),
        // The arena's limit is `mainLimitAmountPerFight`, as on cockfight; the
        // other names are kept in case an older payload sends them.
        maxBet: Number(
          arena.mainLimitAmountPerFight || arena.maxLimitAmountPerFight || arena.maxAmountPerFight || arena.maxBet || 0
        ),
        recentResults: matchDayChanged ? [] : (previousTable && previousTable.recentResults) || [],
        recentDice: matchDayChanged ? [] : (previousTable && previousTable.recentDice) || [],
        recentRounds: matchDayChanged ? [] : (previousTable && previousTable.recentRounds) || [],
        odds: previousTable && previousTable.odds,
        betLimits: previousTable && previousTable.betLimits,
      };
    });

    if (!stateData.tables.some((table) => table.id === stateData.activeTableId)) {
      stateData.activeTableId = stateData.tables.length ? stateData.tables[0].id : '';
      stateData.activeProduct = 'cockfight';
      stateData.quickBetTableId = '';
      stateData.draftBets = {};
      stateData.placedBets = {};
      stateData.betHistory = [];
    }
  },
  UPDATE_TABLE_FIGHT(
    stateData: ITaiXiuState,
    payload: { tableId?: string; fight: Partial<IFight> }
  ): void {
    const arenaId = payload.fight.matchDay && payload.fight.matchDay.arena
      ? payload.fight.matchDay.arena.id
      : '';
    const table = stateData.tables.find(
      (item) => item.id === payload.tableId || (!!arenaId && item.arenaId === arenaId)
    );
    if (!table || !payload.fight.id) return;
    const payloadMatchDayId = payload.fight.matchDay ? payload.fight.matchDay.id : '';
    // A fight from another match day (the previous day's last round, or its
    // late settle) is not this table's round: taken as one, a new match day
    // with no fight yet showed the old day's last round and its result.
    if (!isFightOfMatchDay(payload.fight, table.currentMatchDayId)) return;
    /*
     * A late update for an earlier round on this table (its settle arriving
     * after the next round opened) is not a new round. Taken as one, it swapped
     * the table back to the old fight and wiped the confirmed chips of the
     * running round -- then swapped forward again, wiping them for good. Same
     * match day and a lower fight number: only its result is recorded.
     */
    const isEarlierRound =
      !!table.currentFightId &&
      table.currentFightId !== payload.fight.id &&
      payload.fight.fightNumber !== undefined &&
      !!table.round &&
      Number(payload.fight.fightNumber) < Number(table.round) &&
      (!payloadMatchDayId || !table.currentMatchDayId || payloadMatchDayId === table.currentMatchDayId);
    if (isEarlierRound) {
      recordTableResult(stateData, table, payload.fight);
      return;
    }
    const isNewRound = !!table.currentFightId && table.currentFightId !== payload.fight.id;
    const wasBetOpen = !isNewRound && TAI_XIU_BET_OPEN_STATUSES.includes(String(table.fightStatus));
    table.currentFightId = payload.fight.id;
    if (payload.fight.fightNumber !== undefined) table.round = payload.fight.fightNumber;
    if (payload.fight.status !== undefined) table.fightStatus = payload.fight.status;
    // The countdown runs from betting opening until it closes.
    if (!TAI_XIU_BET_OPEN_STATUSES.includes(String(table.fightStatus))) {
      table.bettingClosesAt = undefined;
    } else if (!wasBetOpen || !table.bettingClosesAt) {
      table.bettingClosesAt = getBettingClosesAt(table, payload.fight);
    }
    table.secondsRemaining = secondsUntil(table.bettingClosesAt);
    if (isNewRound && payload.fight.result === undefined) table.result = undefined;
    if (payload.fight.result !== undefined) {
      table.result = payload.fight.result;
      recordTableResult(stateData, table, payload.fight);
    }
    if (isNewRound && stateData.activeTableId === table.id) {
      stateData.draftBets = {};
      stateData.placedBets = {};
      stateData.betHistory = [];
      stateData.feedback = '';
    }
    if (isNewRound) resetPaneSlipsForTable(stateData, table.id);
    if (!TAI_XIU_BET_OPEN_STATUSES.includes(String(table.fightStatus))) clearDraftsForTable(stateData, table.id);
  },
  SET_TABLE_RESULTS(
    stateData: ITaiXiuState,
    payload: {
      tableId: string;
      results: Array<{ fightId: string; total: number; dice?: number[]; fightNumber?: number }>;
    }
  ): void {
    const table = stateData.tables.find((item) => item.id === payload.tableId);
    if (!table) return;
    const results = payload.results.slice(-ROAD_CHART_RESULT_LIMIT);
    table.recentResults = results.map((item) => item.total);
    table.recentDice = results.map((item) => item.dice);
    table.recentRounds = results.map((item) => item.fightNumber);
    stateData.roadChartFightIds[payload.tableId] = results.map((item) => item.fightId);
  },
  SET_TABLE_ODDS(
    stateData: ITaiXiuState,
    payload: {
      tableId?: string;
      arenaId?: string;
      odds: { [betType: number]: number };
      betLimits?: { [betType: number]: number };
    }
  ): void {
    const table = stateData.tables.find(
      (item) => item.id === payload.tableId || (!!payload.arenaId && item.arenaId === payload.arenaId)
    );
    if (!table) return;
    table.odds = payload.odds;
    table.betLimits = payload.betLimits || {};
  },
  CLEAR_TABLE_FIGHT(stateData: ITaiXiuState, tableId: string): void {
    const table = stateData.tables.find((item) => item.id === tableId);
    if (!table) return;
    table.currentFightId = undefined;
    table.fightStatus = undefined;
    table.result = undefined;
    table.bettingClosesAt = undefined;
    table.secondsRemaining = 0;
    if (stateData.activeTableId === tableId) {
      stateData.draftBets = {};
      stateData.placedBets = {};
      stateData.betHistory = [];
    }
    resetPaneSlipsForTable(stateData, tableId, true);
  },
  UPDATE_TABLE_ROUND(
    stateData: ITaiXiuState,
    payload: { tableId: string; fightId: string; round?: number; secondsRemaining?: number }
  ): void {
    const table = stateData.tables.find((item) => item.id === payload.tableId);
    if (!table) return;
    const isNewRound = !!table.currentFightId && table.currentFightId !== payload.fightId;
    table.currentFightId = payload.fightId;
    if (payload.round !== undefined) table.round = payload.round;
    if (payload.secondsRemaining !== undefined) table.secondsRemaining = payload.secondsRemaining;
    if (isNewRound && stateData.activeTableId === payload.tableId) {
      stateData.draftBets = {};
      stateData.placedBets = {};
      stateData.betHistory = [];
      stateData.feedback = '';
    }
    if (isNewRound) resetPaneSlipsForTable(stateData, payload.tableId);
  },
  TICK_COUNTDOWNS(stateData: ITaiXiuState): void {
    stateData.tables.forEach((table) => {
      if (!table.bettingClosesAt) return;
      const seconds = secondsUntil(table.bettingClosesAt);
      if (seconds !== table.secondsRemaining) table.secondsRemaining = seconds;
    });
  },
  RESET(stateData: ITaiXiuState): void {
    Object.assign(stateData, initialState());
  },
};

/*
 * Validates and posts one slip's draft bets for `table`'s current round. The
 * slip's own mutations are passed in, so the root slip and the pane slips share
 * the one submit path.
 */
const submitSlip = async (options: {
  slip: ITaiXiuSlip;
  table: ITaiXiuTable | undefined;
  skipTriples: boolean;
  token: string;
  setFeedback: (feedback: string) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  onAccepted: (bets: { [selection: string]: number }) => void;
  onRejected: (message: string) => void;
}): Promise<any[]> => {
  const { slip, table, skipTriples, token, setFeedback, setSubmitting, onAccepted, onRejected } = options;
  const selections = Object.keys(slip.draftBets).filter(
    (selection) => !skipTriples || !selection.startsWith('triple-')
  );
  const total = selections.reduce((sum, key) => sum + slip.draftBets[key], 0);
  if (!total) {
    setFeedback('Choose a betting area first');
    return [];
  }
  if (selections.length > MAX_SELECTIONS_PER_BET) {
    setFeedback(`You can select up to ${MAX_SELECTIONS_PER_BET} bets at a time`);
    return [];
  }
  if (slip.isSubmitting) return [];

  if (!table || !table.currentFightId) {
    setFeedback('No active Tài Xỉu round');
    return [];
  }

  const bets = selections.map((selection) => ({
    draftSelection: selection,
    payload: getBetPayload(selection, slip.draftBets[selection], table.currentFightId as string),
  }));
  const invalidBets = bets.filter((bet) => !bet.payload);
  if (invalidBets.length) {
    setFeedback('Some selected bets are invalid');
    return [];
  }
  // Each selection's stake plus what is already placed on it this round,
  // against its limit: the backend counts every ticket of the round, so a
  // stake under the limit on its own can still take it over. Nothing is sent
  // if any is over, so the member can lower it and resubmit the whole slip.
  const isOverLimit = bets.some((bet) => {
    const limit = selectionLimit(table, bet.draftSelection);
    const amount = Number((bet.payload as Omit<ITaiXiuBetPayload, 'odds_type'>).amount) || 0;
    return limit > 0 && amount + (slip.placedBets[bet.draftSelection] || 0) > limit;
  });
  if (isOverLimit) {
    onRejected(MAX_BET_REACHED_POPUP.errorMsg);
    return [];
  }
  const payload = bets.map((bet) => ({ ...(bet.payload as Omit<ITaiXiuBetPayload, 'odds_type'>), odds_type: 'eu' }));
  const submittedBets = selections.reduce(
    (result: { [selection: string]: number }, selection) => {
      result[selection] = slip.draftBets[selection];
      return result;
    },
    {}
  );

  setFeedback('');
  setSubmitting(true);
  try {
    const response = assertBetAccepted(await postData(API_URLS.BET, token, payload));
    onAccepted(submittedBets);
    setFeedback('Bet accepted');
    return response;
  } catch (error) {
    setFeedback('Bets could not be submitted');
    // The API's own reason ({ code, message }), as the cockfight bet shows it.
    const body = error && (error as any).data;
    // A reply with no reason (a bare 5xx, say) still gets the popup, so the
    // member is never left with only the small feedback line.
    onRejected(body && body.message ? `${body.message} E${body.code}` : 'Bets could not be submitted');
    return [];
  } finally {
    setSubmitting(false);
  }
};

const sumBets = (bets: { [selection: string]: number }): number =>
  Object.keys(bets).reduce((sum, selection) => sum + (Number(bets[selection]) || 0), 0);

export const actions = {
  schedulePlacedBetsRestore({ dispatch }: any): void {
    if (restoreTimer) clearTimeout(restoreTimer);
    restoreTimer = setTimeout(() => dispatch('restorePlacedBets'), RESTORE_DEBOUNCE_MS);
  },
  async restorePlacedBets({ state: stateData, rootGetters, commit, dispatch }: any): Promise<void> {
    const token = rootGetters['general/token'];
    if (!token) return;

    // Every table a slip is showing, with the round it is on, not yet restored.
    const slips: Array<{ key: string; tableId: string }> = [
      { key: 'root', tableId: stateData.quickBetTableId || stateData.activeTableId },
      ...Object.keys(stateData.paneSlips).map((paneId) => ({
        key: `pane-${paneId}`,
        tableId: stateData.paneSlips[paneId].tableId,
      })),
    ];
    const pending = slips
      .map((slip) => {
        const table = stateData.tables.find((item: ITaiXiuTable) => item.id === slip.tableId);
        return table && table.currentFightId
          ? { roundKey: `${slip.key}:${table.currentFightId}`, tableId: table.id, fightId: table.currentFightId }
          : null;
      })
      .filter((item): item is { roundKey: string; tableId: string; fightId: string } => !!item)
      .filter((item) => !restoredRounds.has(item.roundKey));
    if (!pending.length) return;

    // Marked before the request, not after: a table re-selected while it is out
    // forgets its round (forgetRestoredSlip), and marking it again on the reply
    // would stop the restore that selection scheduled. A failure unmarks them.
    pending.forEach(({ roundKey }) => restoredRounds.add(roundKey));

    const now = Date.now();
    const params = new URLSearchParams();
    params.append('from_bet_date_time', new Date(now - RESTORE_LOOKBACK_MS).toISOString());
    params.append('to_bet_date_time', new Date(now + 60 * 1000).toISOString());
    params.append('page', '1');
    params.append('page_size', String(RESTORE_PAGE_SIZE));
    params.append('odds_type', rootGetters['general/oddsType']);

    try {
      const response = keysToCamel(await getData(API_URLS.BET_HISTORY, token, params));
      const tickets: any[] = Array.isArray(response) ? response : response.results || [];

      restoreRetries = 0;
      pending.forEach(({ tableId, fightId }) => {
        const bets = tickets
          .filter((ticket) => ticket && ticket.fight && ticket.fight.id === fightId)
          .filter((ticket) => isTicketPending(ticket))
          .reduce((result: { [selection: string]: number }, ticket) => {
            ticketPlacedBets(ticket).forEach(({ selection, amount }) => {
              result[selection] = (result[selection] || 0) + amount;
            });
            return result;
          }, {});
        if (Object.keys(bets).length) commit('RESTORE_PLACED_BETS', { tableId, fightId, bets });
      });
    } catch (error) {
      console.error('[Tai Xiu restore placed bets]', error);
      pending.forEach(({ roundKey }) => restoredRounds.delete(roundKey));
      if (restoreRetries < RESTORE_MAX_RETRIES) {
        restoreRetries += 1;
        if (restoreTimer) clearTimeout(restoreTimer);
        restoreTimer = setTimeout(() => dispatch('restorePlacedBets'), RESTORE_RETRY_DELAY_MS);
      }
    }
  },
  async toggleTableBar({ state: stateData, rootGetters, commit, dispatch }: any): Promise<void> {
    const isOpening = !stateData.isTableBarOpen;
    commit('SET_TABLE_BAR', isOpening);
    if (isOpening && !stateData.tables.length) {
      await dispatch('getArenas', rootGetters['general/config'].language);
    }
  },
  closeTableBar({ commit }: any): void {
    commit('SET_TABLE_BAR', false);
  },
  setQuickPlay({ commit }: any, enabled: boolean): void {
    commit('SET_QUICK_PLAY', enabled);
  },
  async selectTable({ commit, dispatch }: any, tableId: string): Promise<void> {
    commit('SELECT_TABLE', tableId);
    forgetRestoredSlip('root');
    dispatch('schedulePlacedBetsRestore');
    await dispatch('getBetTypeOdds', tableId);
  },
  setViewMode({ commit }: any, mode: 1 | 2): void {
    commit('SET_VIEW_MODE', mode);
  },
  async setPaneTable(
    { commit, dispatch }: any,
    payload: { paneId: TaiXiuPaneId; tableId: string }
  ): Promise<void> {
    commit('SET_PANE_TABLE', payload);
    forgetRestoredSlip(`pane-${payload.paneId}`);
    dispatch('schedulePlacedBetsRestore');
    if (payload.tableId) await dispatch('getBetTypeOdds', payload.tableId);
  },
  addPaneBet(
    { state: stateData, commit }: any,
    payload: { paneId: TaiXiuPaneId; selection: string }
  ): void {
    const slip = stateData.paneSlips[payload.paneId];
    if (!slip) return;
    const table = stateData.tables.find((item: ITaiXiuTable) => item.id === slip.tableId);
    const limit = selectionLimit(table, payload.selection);
    // Capped at the limit; a full selection quietly takes nothing more.
    const chip = allowedChip(slip, limit, payload.selection, paneChip(stateData, slip));
    if (!chip) return;
    commit('PANE_ADD_BET', { ...payload, chip });
  },
  selectPaneChip({ commit }: any, payload: { paneId: TaiXiuPaneId; amount: number }): void {
    commit('PANE_SET_ACTIVE_CHIP', payload);
  },
  undoPaneBet({ commit }: any, paneId: TaiXiuPaneId): void {
    commit('PANE_UNDO_BET', paneId);
  },
  clearPaneBets({ commit }: any, paneId: TaiXiuPaneId): void {
    commit('PANE_CLEAR_BETS', paneId);
  },
  async setQuickPlayTable({ commit, dispatch }: any, tableId: string): Promise<void> {
    commit('SET_QUICK_PLAY_TABLE', tableId);
    forgetRestoredSlip('root');
    dispatch('schedulePlacedBetsRestore');
    await dispatch('getBetTypeOdds', tableId);
  },
  selectCockfight({ commit }: any): void {
    commit('SELECT_COCKFIGHT');
  },
  closeQuickBet({ commit }: any): void {
    commit('CLOSE_QUICK_BET');
  },
  selectChip({ commit }: any, amount: number): void {
    commit('SET_ACTIVE_CHIP', amount);
  },
  addBet({ state: stateData, commit }: any, selection: string): void {
    const tableId = stateData.quickBetTableId || stateData.activeTableId;
    const table = stateData.tables.find((item: ITaiXiuTable) => item.id === tableId);
    const limit = selectionLimit(table, selection);
    // Capped at the limit; a full selection quietly takes nothing more.
    const chip = allowedChip(stateData, limit, selection, stateData.activeChip);
    if (!chip) return;
    commit('ADD_BET', { selection, chip });
  },
  undoBet({ commit }: any): void {
    commit('UNDO_BET');
  },
  clearBets({ commit }: any): void {
    commit('CLEAR_BETS');
  },
  async getArenas({ rootGetters, dispatch }: any, language?: string): Promise<void> {
    const params = new URLSearchParams();
    params.append('has_active_match_day', 'true');
    params.append('lang', language || rootGetters['general/config'].language);
    params.append('game_type', String(ARENA_GAME_TYPE.taiXiu));
    try {
      const response = await getData(
        `${API_URLS.ARENAS}?${params.toString()}`,
        rootGetters['general/token']
      );
      const converted = keysToCamel(response);
      // An empty list, or no list at all, means no Tài Xỉu arena is open.
      const arenas = Array.isArray(converted) ? converted : [];
      arenas.forEach((arena: any) => {
        if (!arena.currentMatchDay) return;
        arena.currentMatchDayId = arena.currentMatchDay.id;
        arena.fightType = arena.currentMatchDay.fightType;
        arena.isReverse = arena.currentMatchDay.isReverse;
        delete arena.currentMatchDay;
      });
      await dispatch('syncArenas', arenas);
      await dispatch('getAllBetTypeOdds');
    } catch (error) {
      console.error('[Arenas]', error);
    }
  },
  async getAllBetTypeOdds({ state: stateData, dispatch }: any): Promise<void> {
    await Promise.all(
      stateData.tables.map((table: ITaiXiuTable) => dispatch('getBetTypeOdds', table.id))
    );
  },
  async getBetTypeOdds({ state: stateData, rootGetters, commit, dispatch }: any, tableId: string): Promise<void> {
    const table = stateData.tables.find((item: ITaiXiuTable) => item.id === tableId);
    const arenaId = table && table.arenaId;
    if (!arenaId) return;

    // The arena's chip row comes from its own endpoint and is wanted at the
    // same moment as its odds, so it is fetched off the same trigger.
    dispatch('general/getArenaBetChips', arenaId, { root: true });

    try {
      const response = await getData(
        `${API_URLS.ARENAS}${arenaId}/${API_URLS.ARENA_BET_TYPE_ODDS}?odds_type=eu`,
        rootGetters['general/token']
      );
      const source =
        response && typeof response === 'object' && !Array.isArray(response)
          ? response.odds || response.data || response
          : {};
      // Each entry is `{ odds, limit }` (`limit` a decimal string or null);
      // a bare number is the older odds-only shape.
      const odds: { [betType: number]: number } = {};
      const betLimits: { [betType: number]: number } = {};
      Object.keys(source).forEach((key) => {
        const betType = Number(key);
        if (!Number.isFinite(betType)) return;
        const entry = source[key];
        const isEntryObject = entry !== null && typeof entry === 'object';
        const value = Number(isEntryObject ? entry.odds : entry);
        if (Number.isFinite(value)) odds[betType] = value;
        const limit = isEntryObject && entry.limit !== null && entry.limit !== undefined ? Number(entry.limit) : NaN;
        if (Number.isFinite(limit) && limit > 0) betLimits[betType] = limit;
      });
      commit('SET_TABLE_ODDS', { tableId: table.id, arenaId, odds, betLimits });
    } catch (error) {
      console.error('[Bet type odds]', error);
    }
  },
  async syncArenas({ state: stateData, commit, dispatch }: any, arenas: IArena[]): Promise<void> {
    const previousArenaIds = stateData.tables.reduce((result: { [tableId: string]: string }, table: ITaiXiuTable) => {
      if (table.arenaId) result[table.id] = table.arenaId;
      return result;
    }, {});
    commit('SYNC_TABLE_ARENAS', arenas);
    // One clock for every table's countdown, started with the first table list.
    if (countdownTicker === null) {
      countdownTicker = window.setInterval(() => commit('TICK_COUNTDOWNS'), 1000);
    }
    const tables = stateData.tables.filter(
      (table: ITaiXiuTable) =>
        table.arenaId && (!table.currentFightId || previousArenaIds[table.id] !== table.arenaId)
    );
    await Promise.all(
      tables.map((table: ITaiXiuTable) =>
        Promise.all([dispatch('getCurrentFight', table.id), dispatch('getRoadChartResults', table.id)])
      )
    );
  },
  async getRoadChartResults({ state: stateData, rootGetters, commit }: any, tableId: string): Promise<void> {
    const table = stateData.tables.find((item: ITaiXiuTable) => item.id === tableId);
    if (!table || !table.currentMatchDayId) return;

    try {
      const response = await getData(API_URLS.FIGHT_RESULT, rootGetters['general/token'], {
        match_day_id: table.currentMatchDayId,
      });
      const data = keysToCamel(response);
      const fights: IFight[] = Array.isArray(data) ? data : data.results || data.fights || [];
      const results = fights
        .map((fight) => ({
          fightId: fight.id,
          fightNumber: Number(fight.fightNumber),
          total: getTaiXiuTotal(fight.result),
          dice: getTaiXiuDice(fight.result),
        }))
        .filter((fight) => fight.total !== undefined)
        .sort((left, right) => left.fightNumber - right.fightNumber)
        .map(({ fightId, fightNumber, total, dice }) => ({
          fightId,
          fightNumber: Number.isFinite(fightNumber) ? fightNumber : undefined,
          total: total as number,
          dice,
        }));
      commit('SET_TABLE_RESULTS', { tableId, results });
    } catch (error) {
      console.error('[Tai Xiu road chart error]', error);
    }
  },
  async getCurrentFight({ state: stateData, rootGetters, commit, dispatch }: any, tableId: string): Promise<void> {
    const table = stateData.tables.find((item: ITaiXiuTable) => item.id === tableId);
    if (!table || !table.arenaId) {
      commit('CLEAR_TABLE_FIGHT', tableId);
      return;
    }

    const params = new URLSearchParams();
    params.append('arena_id', table.arenaId);
    TAI_XIU_CURRENT_ROUND_STATUSES.forEach((status) => params.append('status', status));
    params.append('odds_type', rootGetters['general/oddsType']);
    try {
      const response = await getData(API_URLS.FIGHT_HISTORY, rootGetters['general/token'], params);
      const data = keysToCamel(response);
      const fights: IFight[] = Array.isArray(data) ? data : data.results || [];
      // Only the table's current match day, newest round first: the query is
      // per arena, so with a new match day and no fight yet it handed back the
      // previous day's last (declared) round.
      const fight = fights
        .filter(
          (item) =>
            TAI_XIU_CURRENT_ROUND_STATUSES.indexOf(item.status) >= 0 &&
            isFightOfMatchDay(item, table.currentMatchDayId)
        )
        .sort((left, right) => Number(right.fightNumber) - Number(left.fightNumber))[0];
      if (fight) {
        commit('UPDATE_TABLE_FIGHT', { tableId, fight });
        // The round is known now, so its already-confirmed bets can come back.
        dispatch('schedulePlacedBetsRestore');
      } else {
        commit('CLEAR_TABLE_FIGHT', tableId);
      }
    } catch (error) {
      console.error('[Current fight]', error);
    }
  },
  updateFightFromSocket({ state: stateData, commit, dispatch }: any, fightData: any): void {
    const fight = keysToCamel(fightData) as Partial<IFight>;
    const arenaId = fight.matchDay && fight.matchDay.arena ? fight.matchDay.arena.id : '';
    const table = stateData.tables.find(
      (item: ITaiXiuTable) => (!!arenaId && item.arenaId === arenaId) || item.currentFightId === fight.id
    );
    if (!table) return;
    /*
     * A fight from another match day: either a late update for the previous
     * day (ignored), or the first round of a new match day the table list has
     * not caught up with yet. Reloading the arenas picks up the new match day,
     * which clears the table and fetches its current round.
     */
    if (!isFightOfMatchDay(fight, table.currentMatchDayId)) {
      dispatch('getArenas');
      return;
    }
    const previousFightId = table.currentFightId;
    commit('UPDATE_TABLE_FIGHT', { tableId: table.id, fight });
    // The round can first become known here (socket before the current-fight
    // request, or a refresh mid-round): its confirmed bets are due back too.
    if (table.currentFightId !== previousFightId) dispatch('schedulePlacedBetsRestore');
  },
  async submitBets({ state: stateData, rootGetters, commit, dispatch }: any): Promise<any[]> {
    // The quick-play card bets on its own table. On mobile the two ids agree
    // whenever quick play is open; on desktop the card never moves
    // `activeTableId`, so reading that alone bet on the wrong table.
    const tableId = stateData.quickBetTableId || stateData.activeTableId;
    return submitSlip({
      slip: stateData,
      table: stateData.tables.find((item: ITaiXiuTable) => item.id === tableId),
      // Quick play has no triple market.
      skipTriples: !!stateData.quickBetTableId,
      token: rootGetters['general/token'],
      setFeedback: (feedback) => commit('SET_FEEDBACK', feedback),
      setSubmitting: (isSubmitting) => commit('SET_SUBMITTING', isSubmitting),
      onRejected: (message) =>
        dispatch(
          'menu/updateMenu',
          { isErrorPopupOpen: true, errorHeading: 'Bet not successful', errorMsg: message },
          { root: true }
        ),
      onAccepted: (bets) => {
        commit('SHOW_VIDEO_TOAST', { tableId, kind: 'placed', amount: sumBets(bets) });
        commit('ADD_TABLE_PLACED_BETS', { tableId, bets });
        commit('CLEAR_BETS');
        // Show the new bet in the desktop rail's CURRENT list straight away.
        dispatch('ticket/refreshAfterBet', null, { root: true });
      },
    });
  },
  async submitPaneBets({ state: stateData, rootGetters, commit, dispatch }: any, paneId: TaiXiuPaneId): Promise<any[]> {
    const slip = stateData.paneSlips[paneId];
    if (!slip) return [];
    return submitSlip({
      slip,
      table: stateData.tables.find((item: ITaiXiuTable) => item.id === slip.tableId),
      skipTriples: false,
      token: rootGetters['general/token'],
      setFeedback: (feedback) => commit('PANE_SET_FEEDBACK', { paneId, feedback }),
      setSubmitting: (isSubmitting) => commit('PANE_SET_SUBMITTING', { paneId, isSubmitting }),
      onRejected: (message) =>
        dispatch(
          'menu/updateMenu',
          { isErrorPopupOpen: true, errorHeading: 'Bet not successful', errorMsg: message },
          { root: true }
        ),
      onAccepted: (bets) => {
        commit('SHOW_VIDEO_TOAST', { tableId: slip.tableId, kind: 'placed', amount: sumBets(bets) });
        commit('ADD_TABLE_PLACED_BETS', { tableId: slip.tableId, bets });
        commit('PANE_CLEAR_BETS', paneId);
        // Show the new bet in the desktop rail's CURRENT list straight away.
        dispatch('ticket/refreshAfterBet', null, { root: true });
      },
    });
  },
  updateTableRound(
    { state: stateData, commit, dispatch }: any,
    payload: { tableId: string; fightId: string; round?: number; secondsRemaining?: number }
  ): void {
    const table = stateData.tables.find((item: ITaiXiuTable) => item.id === payload.tableId);
    const previousFightId = table && table.currentFightId;
    commit('UPDATE_TABLE_ROUND', payload);
    if (table && table.currentFightId !== previousFightId) dispatch('schedulePlacedBetsRestore');
  },
  reset({ commit }: any): void {
    commit('RESET');
  },
};

export const getters = {
  videoToast: (stateData: ITaiXiuState) => (tableId: string): ITaiXiuVideoToast | undefined =>
    stateData.videoToasts[tableId],
  tables: (stateData: ITaiXiuState): ITaiXiuTable[] => stateData.tables,
  sessionSlots: (stateData: ITaiXiuState): ISessionSlot[] => stateData.sessionSlots,
  viewMode: (stateData: ITaiXiuState): number => stateData.viewMode,
  // The slot showing in a given pane, so the desktop view can bind each pane
  // independently instead of reading the single `activeProduct`.
  slotForPane: (stateData: ITaiXiuState) => (paneId: PaneId): ISessionSlot | undefined =>
    stateData.sessionSlots.find((slot) => slot.paneId === paneId),
  activeProduct: (stateData: ITaiXiuState): TaiXiuProduct => stateData.activeProduct,
  // The slip a desktop pane is betting on, and the table it is bound to.
  paneSlip: (stateData: ITaiXiuState) => (paneId: TaiXiuPaneId): ITaiXiuPaneSlip =>
    stateData.paneSlips[paneId] || emptyPaneSlip(),
  paneTable: (stateData: ITaiXiuState) => (paneId: TaiXiuPaneId): ITaiXiuTable | undefined => {
    const slip = stateData.paneSlips[paneId];
    return slip ? stateData.tables.find((table) => table.id === slip.tableId) : undefined;
  },
  activeTable: (stateData: ITaiXiuState): ITaiXiuTable | undefined =>
    stateData.tables.find((table) => table.id === stateData.activeTableId),
  quickBetTable: (stateData: ITaiXiuState): ITaiXiuTable | undefined =>
    stateData.tables.find((table) => table.id === stateData.quickBetTableId),
  isTableBarOpen: (stateData: ITaiXiuState): boolean => stateData.isTableBarOpen,
  quickPlayEnabled: (stateData: ITaiXiuState): boolean => stateData.quickPlayEnabled,
  activeChip: (stateData: ITaiXiuState): number => stateData.activeChip,
  draftBets: (stateData: ITaiXiuState): { [selection: string]: number } => stateData.draftBets,
  placedBets: (stateData: ITaiXiuState): { [selection: string]: number } => stateData.placedBets,
  canUndo: (stateData: ITaiXiuState): boolean => stateData.betHistory.length > 0,
  feedback: (stateData: ITaiXiuState): string => stateData.feedback,
  isSubmitting: (stateData: ITaiXiuState): boolean => stateData.isSubmitting,
  isRolling: (stateData: ITaiXiuState): boolean => stateData.isRolling,
  lastResult: (stateData: ITaiXiuState): number | null => stateData.lastResult,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
